import { allKeys } from "@omni/utils/object.utils";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, UseFormProps, useForm } from "react-hook-form";

import { useIsClientSide } from "@/utils/hooks/isClient.hook";

const useOmniForm = <
    TFieldValues extends FieldValues = FieldValues,
    TContext = any,
    TTransformedValues = TFieldValues
>(
    options: UseFormProps<TFieldValues, TContext, TTransformedValues>
) => {
    const isClientSide = useIsClientSide();
    const methods = useForm({ ...options, shouldFocusError: false });
    const [canFocus, setCanFocus] = useState(true);

    const { errors, touchedFields, isSubmitted } = methods.formState;

    const invalidFields = useMemo(() => {
        const errorKeys = allKeys(errors);

        const touchedKeys = allKeys(touchedFields);

        return errorKeys.filter(
            (key) => isSubmitted || touchedKeys.includes(key)
        );
    }, [errors, touchedFields, isSubmitted]);

    const onError = () => {
        setCanFocus(canFocus);
    };

    useEffect(() => {
        if (!isClientSide) {
            return;
        }

        if (invalidFields.length > 0 && canFocus) {
            const elements = document.querySelectorAll<HTMLInputElement>(
                invalidFields.map((key) => `[name="${key}"]`).join(", ")
            );

            if (elements.length > 0) {
                const errorElement = elements[0];
                errorElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
                errorElement.focus({ preventScroll: true });
                setCanFocus(false);
            }
        }
    }, [canFocus, isClientSide, invalidFields]);

    return {
        ...methods,
        handleSubmit: (onValid: Parameters<typeof methods.handleSubmit>[0]) =>
            methods.handleSubmit(onValid, onError)
    };
};

export { useOmniForm };
